import React, {
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import { Button, Popover, Select, Slider } from 'antd';
import { BaseSelectRef } from 'rc-select';

import { FLAT_TYPE_GENERIC } from '../../common/consts';
import {
  formatCurrency,
  formatCurrencyForSlider,
  formatSqrMetersForSlider,
} from '../../common/helpers';
import { getFlatTypeLabel } from '../../common/helpers/flatType.utils';
import { extractFlatTypeFromQuery } from '../../common/helpers/searchQuery.utils';
import useI18n from '../../common/hooks/useI18n';
import { SearchFilter } from '../../common/hooks/useSearchService';
import useOnScreen from '../../common/hooks/useOnScreen';

import {
  priceMax as defaultPriceMax,
  priceMin as defaultPriceMin,
  sqrMetersMax as defaultSqrMetersMax,
  sqrMetersMin as defaultSqrMetersMin,
  typesOptions,
} from '../search/filtersModal';

type Props = {
  filtersButtonRef: React.RefObject<HTMLButtonElement>;
  hasFilters: boolean;
  onFiltersButtonClick: MouseEventHandler;
  applyFilter: (filter: SearchFilter) => void;
  className?: string;
};

const LgDownHiddenDiv = styled.div`
  display: flex;
  height: 100%;

  .ant-select-selection-search {
    display: none;
  }

  @media ${(props) => props.theme.breakpoints.lgd} {
    display: none;
  }
`;

const XlDownHiddenDiv = styled.div`
  @media ${(props) => props.theme.breakpoints.xld} {
    display: none;
  }
`;

const FiltersButton = styled(Button)`
  padding-left: 8px;
  padding-right: 8px;

  @media ${(props) => props.theme.breakpoints.xs} {
    height: 24px;
    padding: 0px 7px;
  }
`;

const FiltersIcon = styled.span<{ hasFilters: boolean }>`
  height: 15px;
  width: 18.4px;
  mask-image: url(/images/filters.svg);
  background-color: ${(props) =>
    props.hasFilters
      ? props.theme.colors.primary
      : props.theme.colors.secondary};
`;

const FiltersText = styled.span`
  @media ${(props) => props.theme.breakpoints.xs} {
    display: none !important;
  }
`;

const SearchBarSuffix: React.FC<Props> = ({
  filtersButtonRef,
  hasFilters,
  onFiltersButtonClick,
  applyFilter,
  className,
}) => {
  const router = useRouter();
  const i18n = useI18n();

  const getTypesValue = useCallback((): string => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const searchParams = new URLSearchParams(window.location.search);
    const types = searchParams.getAll('types');

    if (types.length > 1) {
      return undefined;
    }

    if (types.length === 0) {
      if (
        router.query.flatType &&
        router.query.flatType !== FLAT_TYPE_GENERIC
      ) {
        return extractFlatTypeFromQuery(router.query.flatType as string);
      }

      return undefined;
    }

    return types[0];
  }, [router.query.flatType]);

  const getPriceValue = useCallback((): [number, number] => {
    const defaultValue: [number, number] = [defaultPriceMin, defaultPriceMax];

    if (typeof window === 'undefined') {
      return defaultValue;
    }

    const searchParams = new URLSearchParams(window.location.search);
    const priceMin = searchParams.get('priceMin');
    const priceMax = searchParams.get('priceMax');
    if (priceMin || priceMax) {
      return [
        priceMin ? +priceMin : defaultPriceMin,
        priceMax ? +priceMax : defaultPriceMax,
      ];
    }

    return defaultValue;
  }, []);

  const getSqrMetersValue = useCallback((): [number, number] => {
    const defaultValue: [number, number] = [
      defaultSqrMetersMin,
      defaultSqrMetersMax,
    ];

    if (typeof window === 'undefined') {
      return defaultValue;
    }

    const searchParams = new URLSearchParams(window.location.search);
    const sqrMetersMin = searchParams.get('sqrMetersMin');
    const sqrMetersMax = searchParams.get('sqrMetersMax');
    if (sqrMetersMin || sqrMetersMax) {
      return [
        sqrMetersMin ? +sqrMetersMin : defaultSqrMetersMin,
        sqrMetersMax ? +sqrMetersMax : defaultSqrMetersMax,
      ];
    }

    return defaultValue;
  }, []);

  const [isMounted, setIsMounted] = useState(false);
  const [pricePopoverOpen, setPricePopoverOpen] = useState(false);
  const [price, setPrice] = useState<[number, number]>(getPriceValue());
  const [sqrMetersPopoverOpen, setSqrMetersPopoverOpen] = useState(false);
  const [sqrMeters, setSqrMeters] = useState<[number, number]>(
    getSqrMetersValue()
  );

  const moreFiltersRef = useRef<HTMLDivElement>(null);
  const priceSelectRef = useRef<BaseSelectRef>(null);
  const sqrMetersSelectRef = useRef<BaseSelectRef>(null);

  const moreFiltersOnScreen = useOnScreen([moreFiltersRef]);

  const areMoreFiltersVisible = useMemo(() => {
    return moreFiltersOnScreen >= 0;
  }, [moreFiltersOnScreen]);

  const priceTooltipFormatter = useCallback(
    (p: number, abbreviateThousands = false) =>
      formatCurrencyForSlider(
        p,
        defaultPriceMin,
        defaultPriceMax,
        i18n,
        abbreviateThousands
      ),
    [i18n]
  );

  const sqrMetersTooltipFormatter = useCallback(
    (p: number) =>
      formatSqrMetersForSlider(
        p,
        defaultSqrMetersMin,
        defaultSqrMetersMax,
        i18n
      ),
    [i18n]
  );

  const applyPriceFilter = useCallback(
    (value?: [number, number]) => {
      const newPriceMin =
        value?.[0] === defaultPriceMin ? undefined : value?.[0];
      const newPriceMax =
        value?.[1] === defaultPriceMax ? undefined : value?.[1];
      applyFilter({ priceMin: newPriceMin, priceMax: newPriceMax });
    },
    [applyFilter]
  );

  const applySqrMetersFilter = useCallback(
    (value?: [number, number]) => {
      const newSqrMetersMin =
        value?.[0] === defaultSqrMetersMin ? undefined : value?.[0];
      const newSqrMetersMax =
        value?.[1] === defaultSqrMetersMax ? undefined : value?.[1];
      applyFilter({
        sqrMetersMin: newSqrMetersMin,
        sqrMetersMax: newSqrMetersMax,
      });
    },
    [applyFilter]
  );

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const pricePopoverContent = (
    <Slider
      range
      value={price}
      min={defaultPriceMin}
      max={defaultPriceMax}
      step={10000}
      marks={{
        [defaultPriceMin]: formatCurrency(defaultPriceMin, i18n.activeLocale),
        [defaultPriceMax]: formatCurrency(defaultPriceMax, i18n.activeLocale),
      }}
      tooltip={{
        open: pricePopoverOpen,
        formatter: priceTooltipFormatter,
      }}
      style={{ width: 200 }}
      onChange={(value) => setPrice(value)}
      onAfterChange={applyPriceFilter}
    />
  );

  const sqrMetersPopoverContent = (
    <Slider
      range
      value={sqrMeters}
      min={defaultSqrMetersMin}
      max={defaultSqrMetersMax}
      step={10}
      marks={{
        [defaultSqrMetersMin]: `${defaultSqrMetersMin}m²`,
        [defaultSqrMetersMax]: `${defaultSqrMetersMax}m²`,
      }}
      tooltip={{
        open: sqrMetersPopoverOpen,
        formatter: sqrMetersTooltipFormatter,
      }}
      style={{ width: 200 }}
      onChange={(value) => setSqrMeters(value)}
      onAfterChange={applySqrMetersFilter}
    />
  );

  if (!isMounted) {
    return null;
  }

  return (
    <div className={className}>
      {applyFilter && (
        <LgDownHiddenDiv as="div" ref={moreFiltersRef}>
          <Select
            value={getTypesValue()}
            placeholder={i18n.t('flat.type')}
            style={{ width: 80 }}
            dropdownMatchSelectWidth={false}
            bordered={false}
            showSearch={false}
            allowClear
            options={typesOptions.map((o) => ({
              value: o.value,
              label: i18n.t(getFlatTypeLabel(o.label as string)),
            }))}
            onChange={(value) => applyFilter({ types: value ? [value] : null })}
            onClick={(e) => e.stopPropagation()}
          />
          <XlDownHiddenDiv
            as="div"
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
          >
            <Popover
              content={pricePopoverContent}
              trigger="click"
              overlayInnerStyle={{
                width: 400,
                display: 'flex',
                justifyContent: 'center',
                visibility: pricePopoverOpen ? 'visible' : 'hidden',
              }}
              placement="bottom"
              onOpenChange={(open) => {
                setPricePopoverOpen(open);
                if (!open) {
                  priceSelectRef.current.blur();
                }
              }}
            >
              <Select
                value={
                  price[0] === defaultPriceMin && price[1] === defaultPriceMax
                    ? undefined
                    : `${priceTooltipFormatter(
                        price[0],
                        true
                      )} - ${priceTooltipFormatter(price[1], true)}`
                }
                placeholder={i18n.t('flat.price')}
                style={{ width: 144 }}
                bordered={false}
                allowClear
                onClear={() => {
                  setTimeout(() => setPricePopoverOpen(false), 10);
                  setPrice([defaultPriceMin, defaultPriceMax]);
                  applyPriceFilter();
                }}
                dropdownStyle={{ display: 'none' }}
                ref={priceSelectRef}
              />
            </Popover>
          </XlDownHiddenDiv>
          <XlDownHiddenDiv
            as="div"
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
          >
            <Popover
              content={sqrMetersPopoverContent}
              trigger="click"
              overlayInnerStyle={{
                width: 400,
                display: 'flex',
                justifyContent: 'center',
                visibility: sqrMetersPopoverOpen ? 'visible' : 'hidden',
              }}
              placement="bottom"
              onOpenChange={(open) => {
                setSqrMetersPopoverOpen(open);
                if (!open) {
                  sqrMetersSelectRef.current.blur();
                }
              }}
            >
              <Select
                value={
                  sqrMeters[0] === defaultSqrMetersMin &&
                  sqrMeters[1] === defaultSqrMetersMax
                    ? undefined
                    : `${sqrMetersTooltipFormatter(
                        sqrMeters[0]
                      )} - ${sqrMetersTooltipFormatter(sqrMeters[1])}`
                }
                placeholder={i18n.t('flat.sqrMeters')}
                style={{ width: 144 }}
                bordered={false}
                allowClear
                onClear={() => {
                  setTimeout(() => setSqrMetersPopoverOpen(false), 10);
                  setSqrMeters([defaultSqrMetersMin, defaultSqrMetersMax]);
                  applySqrMetersFilter();
                }}
                dropdownStyle={{ display: 'none' }}
                ref={sqrMetersSelectRef}
              />
            </Popover>
          </XlDownHiddenDiv>
        </LgDownHiddenDiv>
      )}

      <FiltersButton
        type="text"
        icon={
          <FiltersIcon as="span" className="anticon" hasFilters={hasFilters} />
        }
        ref={filtersButtonRef}
        onClick={onFiltersButtonClick}
        onMouseUp={(e) => e.stopPropagation()}
      >
        <FiltersText>
          {i18n.t(
            `search.searchBar.${
              areMoreFiltersVisible ? 'moreFilters' : 'filters'
            }`
          )}
        </FiltersText>
      </FiltersButton>
    </div>
  );
};

export default styled(SearchBarSuffix)`
  border-left: 1px solid ${(props) => props.theme.colors.grey};
  padding-left: 11px;
  height: 48px;
  display: flex;
  align-items: center;

  .ant-select {
    display: flex;
    padding-top: 8px;
    padding-bottom: 8px;
  }

  .ant-select-selector,
  .ant-select-selection-placeholder {
    color: black;
    padding: 0 4px !important;
  }

  .ant-select-arrow,
  .ant-select-clear {
    right: 4px;
  }

  @media ${(props) => props.theme.breakpoints.xs} {
    padding-left: 8px;
  }
`;
